import React, { useMemo, useState, Component } from 'react';
import { ArrowRightIcon } from 'lucide-react';
// ─── Constantes ───────────────────────────────────────────────────────────────
const TASA_ANUAL = 0.045;
const TASA_MENSUAL = TASA_ANUAL / 12;
const COMISION_MENSUAL_UF = 2 * 1.19; // 2.38 UF
function round1(n: number) {
  return Math.round(n * 10) / 10;
}
function calcCuotaBruta(saldoUF: number, n: number): number {
  if (saldoUF <= 0 || n <= 0) return 0;
  const r = TASA_MENSUAL;
  if (r === 0) return saldoUF / n;
  return saldoUF * r / (1 - Math.pow(1 + r, -n));
}
// ─── Slider ───────────────────────────────────────────────────────────────────
interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  primaryLabel: string;
  secondaryLabel?: string;
  onChange: (v: number) => void;
  accentColor?: string;
}
function Slider({
  label,
  value,
  min,
  max,
  step,
  primaryLabel,
  secondaryLabel,
  onChange,
  accentColor = '#2B5FD9'
}: SliderProps) {
  const pct = (value - min) / (max - min) * 100;
  return <div className="space-y-3" data-id="element-1626">
      <div className="flex items-start justify-between gap-4" data-id="element-1627">
        <span className="text-sm font-medium text-[#4B6A9B] leading-snug pt-0.5" data-id="element-1628">
          {label}
        </span>
        <div className="text-right flex-shrink-0" data-id="element-1629">
          <div className="text-2xl font-bold text-[#0A1628] leading-none" data-id="element-1630">
            {primaryLabel}
          </div>
          {secondaryLabel && <div className="text-xs text-[#2B5FD9]/70 mt-0.5 font-medium" data-id="element-1631">
              {secondaryLabel}
            </div>}
        </div>
      </div>

      <div className="relative py-1" data-id="element-1632">
        <input type="range" min={min} max={max} step={step} value={value} onChange={e => onChange(Number(e.target.value))} className="w-full h-2 rounded-full appearance-none cursor-pointer focus:outline-none" style={{
        background: `linear-gradient(to right, ${accentColor} 0%, ${accentColor} ${pct}%, #DBEAFE ${pct}%, #DBEAFE 100%)`
      }} data-id="element-1633" />
      </div>

      <div className="flex justify-between text-xs text-[#93C5FD]" data-id="element-1634">
        <span data-id="element-1635">{min.toLocaleString('es-CL')}</span>
        <span data-id="element-1636">{max.toLocaleString('es-CL')}</span>
      </div>
    </div>;
}
// ─── Fila de desglose ─────────────────────────────────────────────────────────
function Row({
  label,
  sublabel,
  value,
  color = 'text-[#0A1628]',
  divider = false,
  large = false
}: {
  label: string;
  sublabel?: string;
  value: string;
  color?: string;
  divider?: boolean;
  large?: boolean;
}) {
  return <div className={`flex items-start justify-between gap-3 ${divider ? 'pt-4 border-t border-[#DBEAFE]' : ''}`} data-id="element-1637">
      <div className="min-w-0" data-id="element-1638">
        <p className={`${large ? 'text-base font-semibold text-[#0A1628]' : 'text-sm text-[#4B6A9B]'}`} data-id="element-1639">
          {label}
        </p>
        {sublabel && <p className="text-xs text-[#93C5FD] mt-0.5" data-id="element-1640">{sublabel}</p>}
      </div>
      <span className={`font-bold flex-shrink-0 ${large ? 'text-xl' : 'text-sm'} ${color}`} data-id="element-1641">
        {value}
      </span>
    </div>;
}
// ─── Componente principal ─────────────────────────────────────────────────────
export function Simulator() {
  const [valorPropiedad, setValorPropiedad] = useState(10000);
  const [piePct, setPiePct] = useState(5);
  const [duracionMeses, setDuracionMeses] = useState(120);
  const pieUF = useMemo(() => round1(piePct / 100 * valorPropiedad), [piePct, valorPropiedad]);
  const duracionAnos = useMemo(() => round1(duracionMeses / 12), [duracionMeses]);
  const calcs = useMemo(() => {
    // Saldo = valor total - pie inicial
    const saldo = Math.max(0, valorPropiedad - pieUF);
    // CuotaBruta: anualidad financiera sobre el saldo
    const cuotaBruta = round1(calcCuotaBruta(saldo, duracionMeses));
    // Arriendo sobre valor TOTAL de la propiedad
    const arriendoMensual = round1(valorPropiedad * TASA_ANUAL / 12);
    const comision = round1(COMISION_MENSUAL_UF);
    // Cuota neta final
    const resultado = round1(cuotaBruta - arriendoMensual - comision);
    return {
      cuotaPie: cuotaBruta,
      arriendoMensual,
      comision,
      resultado
    };
  }, [valorPropiedad, pieUF, duracionMeses]);
  const positivo = calcs.resultado > 0;
  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section id="simulador" className="py-12 md:py-24 bg-[#EEF4FF]" data-id="element-1642">
      <div className="container mx-auto max-w-6xl px-4" data-id="element-1643">
        {/* Header */}
        <div className="text-center mb-8 md:mb-12" data-id="element-1644">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#2B5FD9]/15 rounded-full mb-4" data-id="element-1645">
            <span className="text-xs font-semibold text-[#2B5FD9]" data-id="element-1646">
              Simulador
            </span>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-[#0A1628] mb-3" data-id="element-1647">
            Simula tu ingreso mensual
          </h2>
          <p className="text-sm md:text-base text-[#4B6A9B] max-w-xl mx-auto" data-id="element-1648">
            Ajusta los valores y ve exactamente cuánto recibirías cada mes, sin
            letra chica.
          </p>
        </div>

        {/* Grid dos columnas */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-start" data-id="element-1649">
          {/* ── Izquierda: Inputs ── */}
          <div className="bg-white rounded-2xl border border-[#DBEAFE] p-6 md:p-8 space-y-8 shadow-sm" data-id="element-1650">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#93C5FD]" data-id="element-1651">
              Configura tu simulación
            </p>

            {/* Slider 1 */}
            <Slider label="Valor comercial de tu propiedad" value={valorPropiedad} min={5000} max={20000} step={500} primaryLabel={`${valorPropiedad.toLocaleString('es-CL')} UF`} onChange={setValorPropiedad} accentColor="#2B5FD9" data-id="element-1652" />

            {/* Slider 2 */}
            <Slider label="Pie en efectivo que quieres recibir" value={piePct} min={0} max={10} step={0.5} primaryLabel={`${piePct}%`} secondaryLabel={pieUF > 0 ? `≈ ${pieUF.toFixed(1)} UF` : 'Sin pago inicial'} onChange={setPiePct} accentColor="#1E4BB8" data-id="element-1653" />

            {/* Slider 3 */}
            <Slider label="Duración del contrato" value={duracionMeses} min={60} max={180} step={12} primaryLabel={`${duracionMeses} meses`} secondaryLabel={`${duracionAnos} años`} onChange={setDuracionMeses} accentColor="#60A5FA" data-id="element-1654" />

            {/* Nota */}
            <div className="bg-[#EEF4FF] rounded-xl p-4 border border-[#DBEAFE]" data-id="element-1655">
              <p className="text-xs text-[#4B6A9B] leading-relaxed" data-id="element-1656">
                <strong className="text-[#0A1628]" data-id="element-1657">¿Cómo funciona?</strong>{' '}
                Creditual adquiere la nuda propiedad de tu vivienda. Tú
                conservas el usufructo — el derecho a vivir en ella. A cambio,
                recibes un pago inicial y cuotas mensuales durante el plazo
                elegido.
              </p>
            </div>
          </div>

          {/* ── Derecha: Resultado ── */}
          <div className="space-y-4" data-id="element-1658">
            {/* Resultado principal */}
            <div className="bg-gradient-to-br from-[#2B5FD9] to-[#1E4BB8] rounded-2xl p-6 md:p-8 shadow-lg" data-id="element-1659">
              <p className="text-sm font-medium text-blue-200 mb-2" data-id="element-1660">
                Cuota mensual que recibirás
              </p>
              <div className="flex items-baseline gap-2 mb-1" data-id="element-1661">
                <span className="text-6xl font-bold leading-none text-white" data-id="element-1662">
                  {Math.abs(calcs.resultado).toFixed(1)}
                </span>
                <span className="text-2xl font-semibold text-blue-300" data-id="element-1663">UF</span>
              </div>
              <p className="text-xs text-blue-300 mt-1" data-id="element-1664">
                Mensual · Reajustado en UF · Heredable
              </p>
            </div>

            {/* Desglose */}
            <div className="bg-white rounded-2xl border border-[#DBEAFE] p-6 md:p-8 shadow-sm" data-id="element-1665">
              <p className="text-xs font-semibold uppercase tracking-widest text-[#93C5FD] mb-5" data-id="element-1666">
                Desglose transparente
              </p>

              <div className="space-y-4" data-id="element-1667">
                <Row label="Cuota estructurada" sublabel="Amortización del pie en el plazo elegido" value={`+ ${calcs.cuotaPie.toFixed(1)} UF`} color="text-[#2B5FD9]" data-id="element-1668" />
                <Row label="Menos arriendo" sublabel={`${valorPropiedad.toLocaleString('es-CL')} UF × 4,5% ÷ 12`} value={`− ${calcs.arriendoMensual.toFixed(1)} UF`} color="text-[#4B6A9B]" data-id="element-1669" />
                <Row label="Menos comisión mensual" sublabel="2 UF + IVA (19%)" value={`− ${calcs.comision.toFixed(1)} UF`} color="text-[#4B6A9B]" data-id="element-1670" />
                <Row label="Recibes cada mes" value={`${positivo ? '' : '−'}${Math.abs(calcs.resultado).toFixed(1)} UF`} color={positivo ? 'text-[#2B5FD9]' : 'text-[#1E4BB8]'} divider large data-id="element-1671" />
              </div>

              {!positivo && <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-3" data-id="element-1672">
                  <p className="text-xs text-[#1E4BB8] leading-relaxed" data-id="element-1673">
                    Con esta configuración el arriendo supera la cuota del pie.
                    Aumenta el pie o el plazo para obtener un resultado
                    positivo.
                  </p>
                </div>}
            </div>

            {/* Resumen parámetros */}
            <div className="bg-white rounded-2xl border border-[#DBEAFE] p-5 shadow-sm" data-id="element-1674">
              <div className="grid grid-cols-3 gap-3 text-center" data-id="element-1675">
                <div data-id="element-1676">
                  <p className="text-base font-bold text-[#0A1628]" data-id="element-1677">
                    {valorPropiedad.toLocaleString('es-CL')}
                  </p>
                  <p className="text-xs text-[#4B6A9B]" data-id="element-1678">UF propiedad</p>
                </div>
                <div className="border-x border-[#DBEAFE]" data-id="element-1679">
                  <p className="text-base font-bold text-[#0A1628]" data-id="element-1680">
                    {pieUF > 0 ? `${pieUF.toFixed(1)} UF` : '—'}
                  </p>
                  <p className="text-xs text-[#4B6A9B]" data-id="element-1681">Pie inicial</p>
                </div>
                <div data-id="element-1682">
                  <p className="text-base font-bold text-[#0A1628]" data-id="element-1683">
                    {duracionAnos} años
                  </p>
                  <p className="text-xs text-[#4B6A9B]" data-id="element-1684">Plazo</p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <button onClick={() => scrollTo('pre-registro')} className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#0A1628] text-white font-semibold rounded-xl hover:bg-[#1E2A3A] transition-colors text-sm md:text-base" data-id="element-1685">
              <span data-id="element-1686">Solicitar evaluación gratuita</span>
              <ArrowRightIcon className="h-4 w-4" data-id="element-1687" />
            </button>

            <p className="text-center text-xs text-[#93C5FD]" data-id="element-1688">
              Simulación referencial. Los valores reales dependen de la tasación
              y condiciones del contrato.
            </p>
          </div>
        </div>
      </div>

      <style data-id="element-1689">{`
        input[type='range']::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: white;
          border: 2.5px solid #2B5FD9;
          box-shadow: 0 1px 6px rgba(43,95,217,0.25);
          cursor: pointer;
        }
        input[type='range']::-moz-range-thumb {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: white;
          border: 2.5px solid #2B5FD9;
          box-shadow: 0 1px 6px rgba(43,95,217,0.25);
          cursor: pointer;
        }
      `}</style>
    </section>;
}